import { useVirtualizer } from "@tanstack/react-virtual";
import type { RefObject } from "react";
import { MIN_DAYS_FOR_GAP } from "@/pages/accounts/account-explorer/touchpoints-table/constants";
// Activity & CollapsedActivity types for accurate type annotations
import type {
  Activity,
  CollapsedActivity,
  GapRow,
} from "@/pages/accounts/account-explorer/touchpoints-table/types";

// ----- Gap height helpers (duplicated from table-row.tsx) -----
const BASE_GAP_HEIGHT = 0; // Keep in sync with table-row.tsx
const BASE_ROW_HEIGHT = 45;

export function calculateGapHeight(days: number | undefined): number {
  if (!days || days <= MIN_DAYS_FOR_GAP) return BASE_GAP_HEIGHT;

  // Cap at 30 days for scaling calculation
  const cappedDays = Math.min(days, 30);

  // Scale from 4 days (minimum gap) to 30 days (maximum)
  // 4 days -> 0px, 30 days -> 90px
  const minDays = MIN_DAYS_FOR_GAP + 1; // 4 days
  const maxDays = 30;
  const maxHeight = BASE_ROW_HEIGHT * 2; // 90px

  // Linear interpolation: (days - minDays) / (maxDays - minDays) * maxHeight
  const scaledHeight =
    ((cappedDays - minDays) / (maxDays - minDays)) * maxHeight;

  return Math.max(0, scaledHeight);
}

/**
 * Generic hook wrapping TanStack Virtual for our tables.
 *
 * parentRef – scrollable element (e.g. <div ref={tableRef}>).
 * data – full collapsedData array so we can estimate each row's height accurately.
 */
export function useVirtualizedRows(
  parentRef: RefObject<HTMLElement | null>,
  data: Array<Activity | CollapsedActivity | GapRow>,
) {
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    // Provide an index-based estimate using gap calculations
    estimateSize: (index: number) => {
      const item = data[index];
      if (!item) return BASE_ROW_HEIGHT;

      if ("kind" in item && item.kind === "gap") {
        return calculateGapHeight((item as GapRow).days);
      }

      // Normal activity rows
      return BASE_ROW_HEIGHT;
    },
    overscan: 10,
  });

  return rowVirtualizer;
}
